import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, Search, FileImage, FileText, Video, Mail } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface MarketingMaterial {
  id: string;
  title: string;
  description: string | null;
  material_type: string;
  file_url: string | null;
  thumbnail_url: string | null;
  dimensions: string | null;
  file_size: number | null;
  tags: string[] | null;
  category: string;
  download_count: number;
}

export const MarketingMaterialsLibrary = () => {
  const [materials, setMaterials] = useState<MarketingMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadMaterials();
  }, []);

  const loadMaterials = async () => {
    try {
      const { data, error } = await supabase
        .from("marketing_materials")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setMaterials(data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load materials");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (material: MarketingMaterial) => {
    try {
      await supabase
        .from("marketing_materials")
        .update({ download_count: material.download_count + 1 })
        .eq("id", material.id);

      if (material.file_url) {
        window.open(material.file_url, "_blank");
        toast.success("Downloaded successfully!");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to download");
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "banner":
        return <FileImage className="w-5 h-5" />;
      case "email":
        return <Mail className="w-5 h-5" />;
      case "video":
        return <Video className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const filteredMaterials = materials.filter((m) =>
    m.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const materialsByType = {
    banner: filteredMaterials.filter((m) => m.material_type === "banner"),
    email: filteredMaterials.filter((m) => m.material_type === "email"),
    social: filteredMaterials.filter((m) => m.material_type === "social"),
    video: filteredMaterials.filter((m) => m.material_type === "video"),
    document: filteredMaterials.filter((m) => m.material_type === "document"),
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Marketing Materials Library</CardTitle>
        <CardDescription>
          Download professional marketing materials to promote your referral link
        </CardDescription>
        <div className="flex gap-2 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search materials..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="all">
          <TabsList>
            <TabsTrigger value="all">All ({filteredMaterials.length})</TabsTrigger>
            <TabsTrigger value="banner">Banners ({materialsByType.banner.length})</TabsTrigger>
            <TabsTrigger value="email">Email ({materialsByType.email.length})</TabsTrigger>
            <TabsTrigger value="social">Social ({materialsByType.social.length})</TabsTrigger>
            <TabsTrigger value="video">Videos ({materialsByType.video.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-4">
            <MaterialGrid materials={filteredMaterials} onDownload={handleDownload} getIcon={getIcon} />
          </TabsContent>

          {Object.entries(materialsByType).map(([type, items]) => (
            <TabsContent key={type} value={type} className="mt-4">
              <MaterialGrid materials={items} onDownload={handleDownload} getIcon={getIcon} />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};

const MaterialGrid = ({
  materials,
  onDownload,
  getIcon,
}: {
  materials: MarketingMaterial[];
  onDownload: (material: MarketingMaterial) => void;
  getIcon: (type: string) => JSX.Element;
}) => {
  if (materials.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No materials found
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {materials.map((material) => (
        <Card key={material.id} className="hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                {getIcon(material.material_type)}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold truncate">{material.title}</h4>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {material.description}
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {material.tags?.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
                {material.dimensions && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {material.dimensions}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-muted-foreground">
                {material.download_count} downloads
              </span>
              <Button
                size="sm"
                onClick={() => onDownload(material)}
                disabled={!material.file_url}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
